<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { visitsAPI, type DailyAvailability, type Visit } from '../services/api';
import { useVisits } from '../composables/useVisits';
import { asDayValue } from '../utils/date';
import ModalShell from './ModalShell.vue';

const props = defineProps<{ open: boolean; visit: Visit | null }>();
const emit = defineEmits<{ close: []; rescheduled: [visit: Visit] }>();

const { t } = useI18n();
const { rescheduleVisit } = useVisits();

const selectedDay = ref('');
const selectedTime = ref('');
const times = ref<string[]>([]);
const isLoadingSlots = ref(false);
const isSubmitting = ref(false);
const submitError = ref<string | null>(null);

const today = asDayValue(new Date());

const loadSlots = async (): Promise<void> => {
  if (!props.visit || !selectedDay.value) return;

  isLoadingSlots.value = true;
  submitError.value = null;
  selectedTime.value = '';

  try {
    const { data } = await visitsAPI.getAvailability(props.visit.id, selectedDay.value);
    const day = data.time.find((entry: DailyAvailability) => entry.date === selectedDay.value);
    times.value = day?.times ?? [];
  } catch {
    times.value = [];
    submitError.value = t('composables.visits.error_slots');
  } finally {
    isLoadingSlots.value = false;
  }
};

// Opening seeds the picker with the visit's current day.
watch(
  () => props.open,
  (open) => {
    if (!open || !props.visit) return;

    const current = new Date(props.visit.visit_at);
    selectedDay.value = asDayValue(current < new Date() ? new Date() : current);
    submitError.value = null;
  },
);

// Watched together so that opening — which also sets the day — loads the slots once
// rather than twice, and so that reopening on an unchanged day still refreshes them.
watch([() => props.open, selectedDay], ([open, day]) => {
  if (open && day) loadSlots();
});

const handleSubmit = async (): Promise<void> => {
  if (!props.visit || !selectedTime.value) return;

  isSubmitting.value = true;
  submitError.value = null;

  const [hours, minutes] = selectedTime.value.split(':').map(Number);
  const target = new Date(`${selectedDay.value}T00:00:00`);
  target.setHours(hours, minutes, 0, 0);

  const result = await rescheduleVisit(props.visit.id, target.toISOString());
  isSubmitting.value = false;

  if (!result.ok) {
    // Surfaced inside the modal rather than behind it, on the list.
    submitError.value = result.message;
    return;
  }

  emit('rescheduled', result.data);
  emit('close');
};
</script>

<template>
  <ModalShell
    :open="open"
    :title="t('visits.reschedule_title')"
    max-width="md"
    @close="emit('close')"
  >
    <div class="space-y-4">
      <div>
        <label class="block text-sm font-medium text-[#9E9080] mb-1">
          {{ t('visits.new_date') }}
        </label>
        <input
          v-model="selectedDay"
          type="date"
          :min="today"
          class="date-input w-full px-4 py-2 border border-[#2A2018] bg-[#1E1E23] text-[#F5F0EA] rounded-xl focus:ring-2 focus:ring-[#D4956A] focus:border-transparent outline-none transition"
        />
      </div>

      <div>
        <label class="block text-sm font-medium text-[#9E9080] mb-2">
          {{ t('visits.pick_time') }}
        </label>

        <p v-if="isLoadingSlots" class="text-sm text-[#524840] py-3">
          {{ t('visits.loading_slots') }}
        </p>
        <p v-else-if="times.length === 0" class="text-sm text-[#524840] py-3">
          {{ t('visits.no_slots') }}
        </p>
        <div v-else class="grid grid-cols-4 gap-2 max-h-56 overflow-y-auto pr-1">
          <button
            v-for="time in times"
            :key="time"
            type="button"
            :class="[
              'py-2 px-2 rounded-xl text-sm font-medium transition',
              selectedTime === time
                ? 'bg-[#D4956A] text-[#0A0A0B]'
                : 'bg-[#1E1E23] text-[#9E9080] hover:bg-[#2A2018]',
            ]"
            @click="selectedTime = time"
          >
            {{ time }}
          </button>
        </div>
      </div>

      <p
        v-if="submitError"
        class="p-3 bg-red-900/20 border border-red-800 rounded-xl text-red-400 text-sm"
      >
        {{ submitError }}
      </p>

      <div class="flex gap-3 pt-2">
        <button
          type="button"
          class="flex-1 py-2.5 px-4 bg-[#1E1E23] hover:bg-[#2A2018] text-[#9E9080] rounded-xl transition font-medium"
          @click="emit('close')"
        >
          {{ t('common.cancel') }}
        </button>
        <button
          type="button"
          :disabled="!selectedTime || isSubmitting"
          class="flex-1 py-2.5 px-4 bg-[#D4956A] hover:bg-[#E8B48A] disabled:bg-[#18181C] disabled:text-[#524840] text-[#0A0A0B] rounded-xl transition font-medium"
          @click="handleSubmit"
        >
          {{ isSubmitting ? t('common.saving') : t('visits.reschedule_submit') }}
        </button>
      </div>
    </div>
  </ModalShell>
</template>
